import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { authApi } from "@/lib/api";

export function SsoButtons({ mode }: { mode: "login" | "register" }) {
  const verb = mode === "login" ? "Sign in" : "Sign up";
  const [pending, setPending] = useState<"linkedin" | "apple" | "google" | "microsoft" | null>(null);

  const redirectFns = {
    linkedin: authApi.linkedinRedirect,
    apple: authApi.appleRedirect,
    google: authApi.googleRedirect,
    microsoft: authApi.microsoftRedirect,
  } as const;
  const providerLabels = {
    linkedin: "LinkedIn",
    apple: "Apple",
    google: "Google",
    microsoft: "Microsoft",
  } as const;

  const startOAuth = async (provider: keyof typeof redirectFns) => {
    setPending(provider);
    try {
      const { url } = await redirectFns[provider]();
      window.location.href = url;
    } catch {
      toast.error(`Could not start ${providerLabels[provider]} sign-in`);
      setPending(null);
    }
  };

  return (
    <div className="grid gap-2">
      <SsoButton
        label={`${verb} with Google`}
        pending={pending === "google"}
        onClick={() => startOAuth("google")}
        icon={
          <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
            <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.68 4.12-5.5 4.12-3.31 0-6-2.74-6-6.12s2.69-6.12 6-6.12c1.88 0 3.15.8 3.87 1.49l2.64-2.55C16.9 3.32 14.7 2.4 12 2.4 6.9 2.4 2.8 6.5 2.8 11.6S6.9 20.8 12 20.8c6.9 0 9.2-4.84 9.2-7.37 0-.5-.05-.87-.12-1.24H12z" />
          </svg>
        }
      />
      <SsoButton
        label={`${verb} with Apple`}
        pending={pending === "apple"}
        onClick={() => startOAuth("apple")}
        icon={
          <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
            <path d="M16.365 12.86c-.02-2.02 1.65-2.99 1.72-3.03-.94-1.37-2.4-1.56-2.92-1.58-1.24-.13-2.42.73-3.05.73-.63 0-1.6-.71-2.63-.69-1.35.02-2.6.79-3.3 2-1.4 2.44-.36 6.05 1.01 8.03.67.97 1.47 2.06 2.5 2.02 1.01-.04 1.39-.65 2.6-.65s1.56.65 2.63.63c1.09-.02 1.78-.99 2.44-1.97.77-1.12 1.09-2.22 1.11-2.28-.02-.01-2.13-.82-2.15-3.24zM14.35 6.9c.56-.68.94-1.62.83-2.56-.81.03-1.79.54-2.37 1.22-.52.6-.97 1.55-.85 2.47.9.07 1.83-.45 2.39-1.13z" />
          </svg>
        }
      />
      <SsoButton
        label={`${verb} with Microsoft`}
        pending={pending === "microsoft"}
        onClick={() => startOAuth("microsoft")}
        icon={
          <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
            <rect x="2" y="2" width="9" height="9" fill="#F25022" />
            <rect x="13" y="2" width="9" height="9" fill="#7FBA00" />
            <rect x="2" y="13" width="9" height="9" fill="#00A4EF" />
            <rect x="13" y="13" width="9" height="9" fill="#FFB900" />
          </svg>
        }
      />
      <SsoButton
        label={`${verb} with LinkedIn`}
        pending={pending === "linkedin"}
        onClick={() => startOAuth("linkedin")}
        icon={
          <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
            <rect width="24" height="24" rx="4" fill="#0A66C2" />
            <path
              fill="#fff"
              d="M7.12 9.4H4.4V19.2h2.72V9.4zM5.76 8.2a1.58 1.58 0 100-3.16 1.58 1.58 0 000 3.16zM19.6 19.2h-2.71v-5.14c0-1.23-.44-2.06-1.53-2.06-.84 0-1.34.56-1.56 1.11-.08.2-.1.47-.1.75v5.34H10.99s.04-8.66 0-9.8h2.71v1.39c.36-.56 1.01-1.35 2.45-1.35 1.79 0 3.13 1.17 3.13 3.68V19.2z"
            />
          </svg>
        }
      />
    </div>
  );
}

function SsoButton({
  label,
  icon,
  onClick,
  pending,
}: {
  label: string;
  icon: ReactNode;
  onClick?: () => void;
  pending?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={pending}
      className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md border border-input bg-background text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-60"
    >
      {icon}
      {pending ? "Redirecting…" : label}
    </button>
  );
}
